import { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";

export default function LaundryServicesList({ services, loading, onOrder, lang = "ar" }) {
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [openItems, setOpenItems] = useState({});
  const isAr = lang === "ar";

  useEffect(() => {
    base44.entities.LaundryCategory.filter({ is_active: true }).then(cats => {
      const sorted = cats.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setCategories(sorted);
      if (sorted.length > 0) setActiveTab(sorted[0].id);
    });
  }, []);

  const tabsRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const checkScroll = () => {
    const el = tabsRef.current;
    if (!el) return;
    setShowScrollHint(el.scrollWidth > el.clientWidth && el.scrollLeft === 0);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [categories]);

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-white dark:bg-gray-800 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  // Services for the active tab category
  const tabServices = services.filter(s => s.category_id === activeTab);

  // Services with no category (fallback)
  const uncategorized = services.filter(s => !s.category_id);

  return (
    <div className="space-y-5">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-6 text-white text-center shadow-xl">
        <div className="text-5xl mb-3">🧺</div>
        <h2 className="text-2xl font-black mb-1">{isAr ? "خدمات المغسلة" : "Laundry Services"}</h2>
        <p className="text-cyan-100 text-sm mb-4">{isAr ? "أسعار شفافة وجودة عالية مع توصيل لباب منزلك" : "Transparent pricing, high quality, delivered to your door"}</p>
        <button
          onClick={onOrder}
          className="bg-white text-cyan-600 font-black px-8 py-3 rounded-2xl shadow-lg hover:bg-cyan-50 transition-all text-sm"
        >
          {isAr ? "📦 اطلب الاستلام الآن" : "📦 Request Pickup Now"}
        </button>
      </div>

      {/* Features */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: "🚗", text: isAr ? "استلام مجاني" : "Free Pickup" },
          { icon: "⚡", text: isAr ? "خدمة سريعة" : "Fast Service" },
          { icon: "🔔", text: isAr ? "تنبيه فوري" : "Instant Alerts" },
        ].map((f, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-3 text-center shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="text-2xl mb-1">{f.icon}</div>
            <p className="text-xs font-bold text-gray-700 dark:text-gray-300">{f.text}</p>
          </div>
        ))}
      </div>

      {/* Category Tabs */}
      {categories.length > 0 && (
        <div>
          {/* Tab Bar */}
          <div className="relative">
          <div
            ref={tabsRef}
            onScroll={checkScroll}
            className="flex flex-nowrap gap-2 overflow-x-auto pb-2 scrollbar-hide"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-bold transition-all ${
                  activeTab === cat.id
                    ? `bg-gradient-to-r ${cat.color || "from-cyan-400 to-blue-500"} text-white shadow-md`
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                }`}
              >
                <span>{cat.icon || "🧺"}</span>
                <span>{isAr ? cat.name : (cat.name_en || cat.name)}</span>
              </button>
            ))}
          </div>
          {/* Scroll hint arrow */}
          {showScrollHint && (
            <div className="absolute top-0 left-0 h-[calc(100%-8px)] w-10 flex items-center justify-center pointer-events-none bg-gradient-to-l from-transparent to-white/0">
              <div className="absolute left-0 top-0 h-[calc(100%-8px)] w-12 bg-gradient-to-l from-transparent to-white dark:to-gray-900 opacity-80 rounded-r-xl" />
              <span className="relative text-lg animate-bounce" style={{ animationDirection: "alternate" }}>‹</span>
            </div>
          )}
          </div>

          {/* Services in active tab */}
          <div className="mt-4 space-y-2">
            {tabServices.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                <p className="text-sm">{isAr ? "لا توجد خدمات في هذه الفئة" : "No services in this category"}</p>
              </div>
            ) : (
              tabServices.map(s => {
                const onSale = s.sale_price && s.sale_start && s.sale_end && today >= s.sale_start && today <= s.sale_end;
                const isOpen = openItems[s.id];

                // Determine which prices are available
                const hasWashIron = !!s.price;
                const hasWashOnly = !!s.price_wash_only;
                const hasIronOnly = !!s.price_iron_only;

                // Determine primary price and its label
                // If wash+iron exists → it's primary. Otherwise first available price is primary.
                let primaryPrice, primaryLabel, primaryColor;
                if (hasWashIron) {
                  primaryPrice = onSale ? s.sale_price : s.price;
                  primaryLabel = isAr ? "غسيل+كوي" : "Wash+Iron";
                  primaryColor = onSale ? "text-amber-500" : "text-cyan-600 dark:text-cyan-400";
                } else if (hasWashOnly) {
                  primaryPrice = s.price_wash_only;
                  primaryLabel = isAr ? "غسيل فقط" : "Wash Only";
                  primaryColor = "text-blue-500 dark:text-blue-400";
                } else if (hasIronOnly) {
                  primaryPrice = s.price_iron_only;
                  primaryLabel = isAr ? "كوي فقط" : "Iron Only";
                  primaryColor = "text-orange-500 dark:text-orange-400";
                } else {
                  primaryPrice = null;
                  primaryLabel = "";
                  primaryColor = "text-gray-400";
                }

                // Secondary prices (shown in dropdown)
                const secondaryPrices = [];
                if (hasWashIron && hasWashOnly) secondaryPrices.push({ label: isAr ? "غسيل فقط" : "Wash Only", price: s.price_wash_only, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/10" });
                if (hasIronOnly) secondaryPrices.push({ label: isAr ? "كوي فقط" : "Iron Only", price: s.price_iron_only, color: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-900/10" });
                // If primary is wash-only, add iron-only as secondary
                if (!hasWashIron && hasWashOnly && hasIronOnly) {
                  secondaryPrices.push({ label: isAr ? "كوي فقط" : "Iron Only", price: s.price_iron_only, color: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-900/10" });
                }

                const hasSecondary = secondaryPrices.length > 0;

                return (
                  <div key={s.id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                    {/* Main row */}
                    <button
                      onClick={() => hasSecondary && toggleItem(s.id)}
                      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-start ${hasSecondary ? "cursor-pointer" : "cursor-default"}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{s.icon || "👕"}</span>
                        <div>
                          <p className="font-bold text-gray-900 dark:text-white text-sm">
                            {isAr ? s.name : (s.name_en || s.name)}
                          </p>
                          {(isAr ? s.description : (s.description_en || s.description)) && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {isAr ? s.description : (s.description_en || s.description)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {primaryPrice !== null ? (
                          <div className="text-end min-w-[90px]">
                            <div className="text-xs text-gray-400 mb-0.5">{primaryLabel}</div>
                            {onSale && hasWashIron ? (
                              <div>
                                <span className={`font-black text-base ${primaryColor}`}>{primaryPrice}</span>
                                <span className="text-gray-400 text-xs"> {isAr ? "ر.س" : "SAR"}</span>
                                <div className="flex items-center gap-1 justify-end">
                                  <span className="text-gray-400 text-xs line-through">{s.price}</span>
                                  <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-600 text-[9px] font-black px-1 py-0.5 rounded-full">🏷️</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <span className={`font-black text-base ${primaryColor}`}>{primaryPrice}</span>
                                <span className="text-gray-400 text-xs"> {isAr ? "ر.س" : "SAR"}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">{isAr ? "تواصل معنا" : "Contact us"}</div>
                        )}
                        {hasSecondary && (
                          <div className={`w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 transition-transform ${isOpen ? "rotate-180" : ""}`}>
                            <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Secondary prices dropdown */}
                    {hasSecondary && isOpen && secondaryPrices.map((sp, i) => (
                      <div key={i} className={`border-t border-gray-100 dark:border-gray-700 ${sp.bg} px-4 py-3 flex items-center justify-between`}>
                        <p className={`text-sm font-semibold ${sp.color} dark:opacity-90`}>{sp.label}</p>
                        <div>
                          <span className={`font-black text-base ${sp.color} dark:opacity-90`}>{sp.price}</span>
                          <span className="text-gray-400 text-xs"> {isAr ? "ر.س" : "SAR"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Uncategorized services (fallback) */}
      {uncategorized.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-muted-foreground">{isAr ? "خدمات أخرى" : "Other Services"}</p>
          {uncategorized.map(s => {
            const onSale = s.sale_price && s.sale_start && s.sale_end && today >= s.sale_start && today <= s.sale_end;
            return (
              <div key={s.id} className="bg-white dark:bg-gray-800 rounded-2xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{s.icon || "👕"}</span>
                  <p className="font-bold text-gray-900 dark:text-white text-sm">{isAr ? s.name : (s.name_en || s.name)}</p>
                </div>
                <div className="text-end">
                  <span className={`font-black text-base ${onSale ? "text-amber-500" : "text-cyan-600 dark:text-cyan-400"}`}>
                    {onSale ? s.sale_price : s.price}
                  </span>
                  <span className="text-gray-400 text-xs"> {isAr ? "ر.س" : "SAR"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {services.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">🧺</div>
          <p>{isAr ? "لا توجد خدمات متاحة حالياً" : "No services available yet"}</p>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="text-center pt-4">
        <button
          onClick={onOrder}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black px-10 py-4 rounded-2xl shadow-lg hover:opacity-90 transition-all text-base"
        >
          {isAr ? "📦 اطلب الاستلام الآن" : "📦 Request Pickup Now"}
        </button>
        <p className="text-xs text-gray-500 mt-2">{isAr ? "لا تحتاج لتحديد الخدمات مسبقاً" : "No need to select services in advance"}</p>
      </div>
    </div>
  );
}